import { LoadingOutlined } from "@ant-design/icons";
import {
  AuthenticatedTemplate,
  useMsal
} from "@azure/msal-react";
import { ConfigProvider, Spin } from "antd";
import { closeAppBackdrop, setAppNavigate, showAppBackdrop } from "features/app/store/slice";
import { loginSuccess, userSuccess } from "features/auth/store/slice";
import { SnackbarOrigin, SnackbarProvider } from "notistack";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { getLocalItem, setLocalItem } from "utils";
import {
  API_BASE_URL,
  APP_NOTIFICATION_HORIZONTAL,
  APP_NOTIFICATION_MAX_STACK,
  APP_NOTIFICATION_VERTICAL,
  APP_REFRESH_TOKEN_NAME,
  APP_TOKEN_NAME
} from "utils/constants";
import HttpRequest from "utils/httpRequest";
import Backdrop from "views/components/Bases/Backdrop";
import Layout from "views/includes/Layout";
import "./App.css";
import { graphConfig, loginRequest } from "./views/includes/authConfig";

function App() {
  const { instance, accounts } = useMsal();
  const { pathname } = useLocation();

  const activeAccount = instance.getActiveAccount();
  const access_token = getLocalItem(APP_TOKEN_NAME);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dispatch = useDispatch();

  const setAccessToken = async () => {
    if (accounts.length > 0 && access_token) {
      setIsLoading(false);
    } else {
      instance
        .handleRedirectPromise()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .then(async (authResult) => {
          if (!authResult) {
            await instance.loginRedirect(loginRequest);
          } else {
            dispatch(showAppBackdrop());
            setIsLoading(true);
            const headers = new Headers();
            const bearer = `Bearer ${authResult.accessToken}`;

            headers.append("Authorization", bearer);

            const options = {
              method: "GET",
              headers: headers,
            };

            await fetch(graphConfig.graphMeEndpoint, options)
              .then((response) => response.json())
              .then(async (response) => {
                await HttpRequest()
                  .post(`${API_BASE_URL}/auth/login`, {
                    email: response.mail,
                    first_name: response.givenName,
                    last_name: response.surname,
                    display_name: response.displayName,
                    azure_id: response.id
                  })
                  .then(async (response) => {
                    setLocalItem(
                      APP_TOKEN_NAME,
                      response.data?.access_token as string
                    );
                    setLocalItem(
                      APP_REFRESH_TOKEN_NAME,
                      response.data?.refresh_token as string
                    );
                    dispatch(loginSuccess({
                      role: response.data.role,
                      email: response.data.username,
                      user_name: response.data.username.split("@")[0],
                      department: response.data.department,
                      permission: response.data.permission,
                      active: response.data.is_active,
                      first_name: response.data.firstname,
                      last_name: response.data.lastname,
                      team_name: response.data.teamname,
                      audit: response.data.audit,
                      webosapi: response.data.webosapi,
                      securityLog: response.data.securityLog
                    }))
                    dispatch(closeAppBackdrop());
                    setIsLoading(false);
                  })
                  .catch((e) => {
                    dispatch(closeAppBackdrop());
                    setIsLoading(false);
                  });
              });
          }
        });
    }
  };

  useEffect(() => {
    setAccessToken();
  }, [activeAccount]);

  useEffect(() => {
    if (access_token) {
      setIsLoading(true);
      HttpRequest()
        .get(`${API_BASE_URL}/auth/user`)
        .then(async (response) => {
          dispatch(userSuccess({
            role: response.data.role,
            email: response.data.username,
            user_name: response.data.username.split("@")[0],
            department: response.data.department,
            permission: response.data.permission,
            active: response.data.is_active,
            first_name: response.data.firstname,
            last_name: response.data.lastname,
            team_name: response.data.teamname,
            audit: response.data.audit,
            webosapi: response.data.webosapi,
            securityLog: response.data.securityLog
          }))
          dispatch(closeAppBackdrop());
          setIsLoading(false);
        })
        .catch((e) => {
          dispatch(closeAppBackdrop());
          setIsLoading(false);
        });
    }
  }, [])

  useLayoutEffect(() => {
    dispatch(setAppNavigate(pathname));
  }, [pathname])

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#2e90fa",
          colorError: "#f04438",
          colorSuccess: "#12b763",
          colorWarning: "#f79009",
          colorBgLayout: "#f2f4f7",
        },
      }}
    >
      <Backdrop />
      <Suspense fallback={<Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />} >
        <SnackbarProvider
          maxSnack={APP_NOTIFICATION_MAX_STACK}
          anchorOrigin={
            {
              vertical: APP_NOTIFICATION_VERTICAL,
              horizontal: APP_NOTIFICATION_HORIZONTAL,
            } as SnackbarOrigin
          }
        >
          <AuthenticatedTemplate>
            {isLoading ? <></> : <Layout />}
          </AuthenticatedTemplate>
        </SnackbarProvider>
      </Suspense>
    </ConfigProvider>
  );
}

export default App;
